import type { UploadFormat } from '@tabservice/tabfile-client';

export interface FormatMeta {
  /** Full name for the tile. */
  label: string;
  /** Short tag used in tables. */
  tag: string;
  description: string;
  /** What the file input accepts. */
  accept: string;
  extensions: string[];
  contentType: 'text/csv' | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' | 'text/plain' | 'application/xml';
}

export const formatMeta: Record<UploadFormat, FormatMeta> = {
  csv: {
    label: 'CSV',
    tag: 'CSV',
    description: 'One row per return. Column names are matched automatically.',
    accept: '.csv',
    extensions: ['.csv'],
    contentType: 'text/csv',
  },
  xlsx: {
    label: 'Excel',
    tag: 'XLSX',
    description: 'A spreadsheet, one row per return, read from the first sheet.',
    accept: '.xlsx,.xls',
    extensions: ['.xlsx', '.xls'],
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  pub1220: {
    label: 'IRS 1220 file',
    tag: '1220',
    description: 'The FIRE format, accepted exactly as your system produces it.',
    accept: '.txt,.dat',
    extensions: ['.txt', '.dat'],
    contentType: 'text/plain',
  },
  irisxml: {
    label: 'IRIS XML',
    tag: 'XML',
    description: 'A complete IRIS transmission, validated against the IRS schema and sent under the Tab Service transmitter header.',
    accept: '.xml',
    extensions: ['.xml'],
    contentType: 'application/xml',
  },
};

/** Short tag for an e-file, including the inline JSON route. */
export const filingFormatTag: Record<string, string> = {
  json: 'JSON',
  csv: 'CSV',
  xlsx: 'XLSX',
  pub1220: '1220',
  irisxml: 'XML',
};

export function formatFromFileName(fileName: string): UploadFormat | null {
  const lower = fileName.toLowerCase();
  for (const [format, meta] of Object.entries(formatMeta) as [UploadFormat, FormatMeta][]) {
    if (meta.extensions.some((extension) => lower.endsWith(extension))) return format;
  }
  return null;
}

export function fileMatchesFormat(fileName: string, format: UploadFormat): boolean {
  const lower = fileName.toLowerCase();
  return formatMeta[format].extensions.some((extension) => lower.endsWith(extension));
}
